/**
 * The Windows Media Player playlist.
 *
 * Five ordinary mp3s in `public/assets/media/`, encoded at 128 kbps CBR joint
 * stereo — the way everything was ripped in 1997, and small enough that opening
 * the player does not mean waiting on forty megabytes first. They run through
 * the same gain and analyser nodes the synth below uses, which is why the
 * transport, the seek bar, Winamp's equaliser and the visualiser all work on
 * them without knowing the difference.
 *
 * ── Adding a track ──────────────────────────────────────────────────────────
 * Drop the file in `public/assets/media/` and reference it as
 * `/assets/media/<name>.mp3`, or point `src` at any CORS-enabled URL. Nothing
 * else in the codebase needs to change:
 *
 *   { id: 'demo', title: 'Demo', artist: 'Someone',
 *     src: '/assets/media/demo.mp3', duration: 184, bitrate: 128 }
 *
 * `duration` and `bitrate` are only what the display has to show before the
 * file arrives; the real duration replaces the hint once metadata loads.
 *
 * ── The synth ───────────────────────────────────────────────────────────────
 * A track may instead carry note *data*, which `src/apps/mediaplayer/synth.ts`
 * performs on a couple of oscillators — a few kilobytes for a whole piece, and
 * the only kind of track that costs nothing to ship. Positions and lengths are
 * in *beats*, not seconds, so changing `tempo` re-times the piece; pitches are
 * scientific note names (`A4`, `F#3`, `Bb2`) and the `hat` voice ignores its
 * pitch. Write the last bar leading back into the first — the engine loops a
 * piece without a seam.
 */

/**
 * The four voices the synth knows about: a square lead, a soft filtered pad,
 * a triangle bass and a noise hi-hat.
 */
export type Voice = "lead" | "pad" | "bass" | "hat";

export interface Note {
  voice: Voice;
  /** Scientific pitch, e.g. `A4`, `F#3`, `Bb2`. Ignored by the `hat` voice. */
  pitch: string;
  /** Onset, in beats from the start of the piece. */
  startBeat: number;
  /** Length, in beats. */
  beats: number;
  /** Relative loudness, 0..1. Defaults to 1. */
  gain?: number;
}

interface TrackBase {
  /** Stable; the player remembers it across re-opens. */
  id: string;
  title: string;
  artist: string;
  album?: string;
}

/** A piece the synth performs from note data. */
export interface SynthTrack extends TrackBase {
  /** Beats per minute. */
  tempo: number;
  /** Total length in beats — where the piece ends, not where the last note does. */
  lengthBeats: number;
  notes: Note[];
  src?: undefined;
}

/** An ordinary audio file, played through the same mixer. */
export interface AudioTrack extends TrackBase {
  src: string;
  /**
   * Seconds, floored — the real value replaces it once metadata loads, and a
   * rounded hint makes the listed time jump a second when it does.
   */
  duration: number;
  /** kbps, for Winamp's display. Exact, since these are constant-rate. */
  bitrate?: number;
  notes?: undefined;
}

export type Track = SynthTrack | AudioTrack;

export function isSynthTrack(track: Track): track is SynthTrack {
  return track.src === undefined;
}

/** Playing length in seconds. */
export function trackDuration(track: Track): number {
  return isSynthTrack(track)
    ? (track.lengthBeats * 60) / track.tempo
    : track.duration;
}

/**
 * What the player loads on first open, in order — chronological, 1994 to 2000,
 * which is near enough the years this desktop is pretending to be.
 *
 * Thirty seconds each, from the top, fading out over the last second and a
 * half. That is what a preview was in 1999, and it keeps the whole playlist
 * to a couple of megabytes rather than eighteen.
 */
export const playlist: Track[] = [
  {
    id: "one-week",
    title: "One Week",
    artist: "Barenaked Ladies",
    album: "Stunt",
    src: "/assets/media/barenaked-ladies-one-week.mp3",
    duration: 30,
    bitrate: 128,
  },
  {
    id: "dont-say-you-love-me",
    title: "Don't Say You Love Me",
    artist: "M2M",
    album: "Shades of Purple",
    src: "/assets/media/m2m-dont-say-you-love-me.mp3",
    duration: 30,
    bitrate: 128,
  },
  {
    id: "love-to-love-you",
    title: "Love to Love You",
    artist: "The Corrs",
    album: "In Blue",
    src: "/assets/media/the-corrs-love-to-love-you.mp3",
    duration: 30,
    bitrate: 128,
  },
  {
    id: "juicy",
    title: "Juicy",
    artist: "The Notorious B.I.G.",
    album: "Ready to Die",
    src: "/assets/media/notorious-big-juicy.mp3",
    duration: 30,
    bitrate: 128,
  },
  {
    id: "kryptonite",
    title: "Kryptonite",
    artist: "3 Doors Down",
    album: "The Better Life",
    src: "/assets/media/3-doors-down-kryptonite.mp3",
    duration: 30,
    bitrate: 128,
  },
];

/** The playlist's display name, shown above the list. */
export const playlistName = "Napster Hits";

/** The playlist's display name, and so the name of the file it saves as. */
export const playlistFileName = `${playlistName}.m3u`;

/**
 * What a track is called as a file in My Music. One function, because the
 * folder listing and the `.m3u` that indexes it have to agree on the name —
 * a playlist whose entries do not match the files beside it is a playlist of
 * missing tracks.
 */
export function trackFileName(track: Track): string {
  return `${track.title}.mp3`;
}

/**
 * The playlist as an actual `.m3u`, generated from the list above.
 *
 * The extended form Winamp wrote: `#EXTM3U` on top, then a `#EXTINF` carrying
 * seconds and `artist - title` ahead of each entry. Bare filenames rather than
 * paths, because the tracks sit in the same folder as the playlist, which is
 * how Winamp saved one from a single directory. CRLF, as a file written on
 * Windows in 1999 would be.
 */
export function playlistM3u(tracks: readonly Track[] = playlist): string {
  const lines = ['#EXTM3U'];
  for (const track of tracks) {
    lines.push(`#EXTINF:${Math.round(trackDuration(track))},${track.artist} - ${track.title}`);
    lines.push(trackFileName(track));
  }
  return `${lines.join('\r\n')}\r\n`;
}
